import { GameState } from './GameState';
import { EventBus, GameEvents } from '../core/EventBus';
import { AssistantMessage } from '../data/types';
import { GameConfig } from '../core/GameConfig';

/** 消息源类型：入队即算一条未读 */
export interface QueueOptions {
    kind?: AssistantMessage['kind'];
    /** 为 true 时直接标记已读（不占悬浮角标；教学/顺带播报用） */
    silent?: boolean;
}

/** 小助理消费/未读快照（供悬浮按钮 / 对话框渲染） */
export interface AssistantStatus {
    /** 累计消息数 */
    total: number;
    /** 已读（对话框已消费）条数 */
    readCount: number;
    /** 未读数 = total − readCount */
    unread: number;
    hasUnread: boolean;
}

/**
 * 单例：小助理系统。生命周期挂在 app（GameApp）创建时 init()，全局一个实例。
 */
export class AssistantSystem {
    private static instance: AssistantSystem;
    private inited = false;

    private constructor() {}

    static getInstance(): AssistantSystem {
        if (!this.instance) this.instance = new AssistantSystem();
        return this.instance;
    }

    /** 当前绝对月份（无 player 时返回 0，避免主菜单阶段入队崩） */
    private nowAbs(): number {
        const g = GameState.getInstance();
        return g.player ? g.player.year * 12 + g.player.month : 0;
    }

    /**
     * 称呼玩家：直接叫昵称（2026-10-25 茄子令「不带姐了，就叫昵称」）。trim 防尾空格；
     * 缺省（旧档没填昵称）回空串。⛔ 恢复「xx姐」口径（历史：曾是「{昵称}姐」，被「直呼昵称」令取代）。
     */
    term(): string {
        const g = GameState.getInstance();
        return g.player ? (g.player.nickname ?? '').trim() : '';
    }

    /** 称呼展示（供 UI 拼接台词）：{ nickname, term }（term 与本.term() 同一口径） */
    greeting(): { nickname: string; term: string } {
        const g = GameState.getInstance();
        const nick = g.player ? (g.player.nickname ?? '').trim() : '';
        return { nickname: nick, term: nick };
    }

    /**
     * 台词拼接统一口（2026-10-28 茄子令：~ 记号语义反转＝句首拼昵称标记）：
     * - 行首 `~`（台词表记号，玩家不可见）＝这句句首拼「昵称，」；昵称为空则不拼，只剥 ~ 直接正文。
     * - 文案中 `{player}` 占位符照旧替换（昵称优先、空昵称 `{player}，` 整段吃掉回退本名）。
     *   两者不叠加——现行表里带 ~ 的句子都不含 {player}（intro 用 {player}、无 ~）。
     * 台词表（docs/夏夏台词-文案填写表.md）是唯一事实源：哪句拼哪句不拼看行首有没有 `~`。
     * ⛔ 自加空格（拼接紧贴）；旧「无条件前缀」「~＝豁免不拼」两案均已废弃。
     */
    joinTerm(text: string): string {
        let t = text;
        if (t.startsWith('~')) {
            t = t.slice(1);
            const nick = this.term();
            if (nick) t = `${nick}，${t}`;
        }
        return this.replacePlayer(t);
    }

    /** `{player}` 替换本体（joinTerm 委托于此；intro 等占位符句也走这同一把替换尺） */
    replacePlayer(text: string): string {
        const nick = this.term();
        if (nick) return text.replace(/\{player\}/g, nick);
        return text.replace(/\{player\}，/g, '').replace(/\{player\}/g, this.fallbackName());
    }

    /** 昵称为空时的兜底名：本名 → 「你」（与 plotStage 插值同口径） */
    private fallbackName(): string {
        const g = GameState.getInstance();
        return g.player?.name || '你';
    }

    /**
     * 初始化：订阅事件。
     * 各事件文案是「夏夏的一句话」，面向玩家视角；玩家名占位符 `{player}` 写才拼（joinTerm=replacePlayer）。
     *
     * 【关键设计】
     * - 合同作废提醒：由 ScriptSystem.sweepOverdueContracts() 直接通过 assistantSystem.push() 发送
     * - 避免通过 EventBus.LOG_ADDED 事件间接通知，防止重复提醒
     * - experience-manager-notify 只处理非作废类型通知
     */
    init(): void {
        if (this.inited) return;
        this.inited = true;

        // 经纪人通知（月结算由 NoticeSystem.push 产生）→ 小助理播报 + 仍入经理档案历史。
        // 过滤作废类型：kind='deadline' 的合同作废由 ScriptSystem 直接推送，避免重复
        document.addEventListener('assistant-manager-notify', ((e: Event) => {
            const detail = (e as CustomEvent<{ kind: string; text: string }>).detail;
            if (detail && detail.kind !== 'deadline') {
                this.push(detail.text, { kind: detail.kind as AssistantMessage['kind'] });
            }
        }) as EventListener);

        // LOG_ADDED 事件已全库清退（2026-10-27 茄子令「没人接收的文案不用发」）：发射处全删，
        // 原空订阅与事件枚举一并移除；业务播报一律走 assistantSystem.push 直推。

        // 周目结束：清空队列（结算页无悬浮按钮）
        EventBus.on(GameEvents.GAME_OVER, () => this.clear());
    }

    /** 入队一条播报。silent=true 立即标记已读。队列保留最近 ASSISTANT_QUEUE_MAX 条。 */
    push(text: string, opts: QueueOptions = {}): void {
        const g = GameState.getInstance();
        if (!g.player) return; // 主菜单阶段无 player，不播报
        const p = g.player;
        if (!Array.isArray(p.assistantMessages)) p.assistantMessages = [];
        p.assistantMessages.push({
            kind: opts.kind ?? 'tut',
            month: this.nowAbs(),
            // 称呼与正文拼接走 joinTerm 统一口（2026-10-25 台词清洗批）：称呼为空连逗号吃掉，⛔ 自加空格
            text: this.joinTerm(text),
        });
        if (p.assistantMessages.length > GameConfig.ASSISTANT_QUEUE_MAX) {
            const overflow = p.assistantMessages.length - GameConfig.ASSISTANT_QUEUE_MAX;
            p.assistantMessages = p.assistantMessages.slice(-GameConfig.ASSISTANT_QUEUE_MAX);
            p.assistantReadCount = Math.max(0, (p.assistantReadCount ?? 0) - overflow);
        }
        if (opts.silent) this.markRead(1);
        EventBus.emit(GameEvents.ASSISTANT_QUEUED);
    }

    /**
     * 当前待展示消息（readCount 指向下一条未读；只读取不消费）。
     * 对话框打开即展示这条，玩家点「知道了」才 acknowledge 计入已读。
     */
    current(): AssistantMessage | null {
        const g = GameState.getInstance();
        if (!g.player) return null;
        const p = g.player;
        if (!Array.isArray(p.assistantMessages)) return null;
        const read = p.assistantReadCount ?? 0;
        if (read >= p.assistantMessages.length) return null;
        return p.assistantMessages[read];
    }

    /** 已读 +1（对话框逐条推进时调用：点「知道了」消费当前展示消息） */
    acknowledge(): void {
        const g = GameState.getInstance();
        if (!g.player) return;
        const p = g.player;
        if (!Array.isArray(p.assistantMessages)) return;
        p.assistantReadCount = Math.min(p.assistantMessages.length, (p.assistantReadCount ?? 0) + 1);
        EventBus.emit(GameEvents.ASSISTANT_CONSUMED);
    }

    /** 直接标记已读 N 条（跳过/手动关闭用） */
    markRead(n: number): void {
        const g = GameState.getInstance();
        if (!g.player) return;
        const p = g.player;
        if (!Array.isArray(p.assistantMessages)) p.assistantMessages = [];
        p.assistantReadCount = Math.min(p.assistantMessages.length, (p.assistantReadCount ?? 0) + n);
        EventBus.emit(GameEvents.ASSISTANT_CONSUMED);
    }

    /** 未读快照（主菜单等无 player 时返回空） */
    status(): AssistantStatus {
        const g = GameState.getInstance();
        if (!g.player) return { total: 0, readCount: 0, unread: 0, hasUnread: false };
        const p = g.player;
        if (!Array.isArray(p.assistantMessages)) p.assistantMessages = [];
        const total = p.assistantMessages.length;
        const read = p.assistantReadCount ?? 0;
        const unread = Math.max(0, total - read);
        return { total, readCount: read, unread, hasUnread: unread > 0 };
    }

    /** 读取第 idx 条历史（供 UI 回放；越界返回 null） */
    at(idx: number): AssistantMessage | null {
        const g = GameState.getInstance();
        if (!g.player) return null;
        const p = g.player;
        if (!Array.isArray(p.assistantMessages)) return null;
        return p.assistantMessages[idx] ?? null;
    }

    /** 周目结束清理（队列即状态，随存档整体迁移，此处仅保证无残余监听） */
    clear(): void {
        // 消息在 PlayerState，随新周目重置；无独立运行时态
    }
}

/** 导出单例 */
export const assistantSystem = AssistantSystem.getInstance();